import { CSSProperties, ReactElement, memo } from 'react';
import { Box, Button, openModal, Popover } from '@ff-it/ui';
import { useField, useForm } from '@ff-it/form';
import { AddButton, BlockWidget, ClientTitle, RemoveButton, RowLinkStateLoader } from 'components';
import { EnterpriseLink } from 'components/EnterpriseLink';
import { Icon } from 'components/Icon';
import { EntityMap } from '../types';
import { SubTitle, TitleContainer } from 'components/TitleContainer';
import { oddRow } from 'styles/style.css';
import { RowContainer } from './RowContainer';
import { Linkable } from './types';
import { ManualOverride } from './ManualOverride';
import { sepFormat } from 'utilities';
import Big from 'big.js';
import { SupplierTitle } from 'modules/supplier/supplier/components';
import { SourceInvoice } from './SourceInvoice';
import { differingSource, sourceContainer } from './style.css';
import { PeriodIntersection } from 'components/PeriodIntersection';

type AmountProps = {
  name: string;
  bound: Big | null;
};

function Amount({ name, bound }: AmountProps): ReactElement {
  const {
    meta: { submitError, modifiedSinceLastSubmit },
  } = useField(name, { subscription: { submitError: true, modifiedSinceLastSubmit: true } });

  return submitError && !modifiedSinceLastSubmit ? (
    <Box fontWeight="bold" color="error500" title={submitError}>
      {sepFormat(bound?.toFixed(), true, false)}
    </Box>
  ) : (
    <Box>{sepFormat(bound?.toFixed(), true, false)}</Box>
  );
}

type RowProps = {
  index: number;
  style: CSSProperties;
  data: {
    rows: Linkable[];
    entities: EntityMap;
    disabled: boolean;
    visibleKeys?: Set<string>;
  };
  isScrolling?: boolean | undefined;
};

export const Row = memo(
  function Row({ index, data: { disabled, entities, rows, visibleKeys }, style }: RowProps): ReactElement {
    const form = useForm();
    const row = rows[index];
    const {
      id,
      campaign_id,
      client_id,
      payer_id,
      product_id,
      provider_id,
      activity_id,
      block_id,
      state,
      target,
      source,
      rate,
      invoiceable,
      invoiced,
      balance,
      bound,
    } = row;

    const key = `bound._${id}`;
    const balanceControl =
      !disabled && bound == null ? (
        <AddButton
          testId={`link-${id}`}
          onClick={() => {
            form.change(key, balance.toFixed());
          }}
        />
      ) : undefined;

    const boundControl =
      !disabled && bound !== null ? (
        <RemoveButton
          testId={`unlink-${id}`}
          onClick={() => {
            form.change(key, undefined);
          }}
        />
      ) : undefined;

    const manualControl =
      !disabled && bound !== null ? (
        <Button
          testId={`link-manual-${id}`}
          variant="ghost"
          type="button"
          size="sm"
          icon="pencil"
          onClick={() =>
            openModal((props) => <ManualOverride {...props} row={row} />).then((amount) => {
              amount && form.change(key, amount);
            })
          }
        />
      ) : undefined;

    const sourceElement = (
      <Popover
        placement="auto"
        offsetOptions={6}
        content={<RowLinkStateLoader id={row.id} direction={state.direction} kind="net" />}
      >
        <Box className={sourceContainer} role="button">
          <PeriodIntersection intersection={state.intersection} direction={state.direction} />
          <TitleContainer title={target}>
            {target !== source ? (
              <SubTitle className={differingSource} testId="source">
                {source}
              </SubTitle>
            ) : undefined}
          </TitleContainer>
        </Box>
      </Popover>
    );

    return (
      <RowContainer
        testId={id}
        style={style}
        visibleKeys={visibleKeys}
        className={index % 2 === 0 ? oddRow : undefined}
        rowLink={
          <EnterpriseLink to={`/planning/campaigns/${campaign_id}/planning/${block_id}?rowId=${id}`}>
            <Icon size="sm" icon="link" />
          </EnterpriseLink>
        }
        client={<ClientTitle client={entities.company[client_id]} payer={entities.company[payer_id]} />}
        supplier={
          <SupplierTitle
            product={entities.product[product_id]}
            provider={entities.company[provider_id]}
            activity={entities.activity[activity_id]}
          />
        }
        block={<BlockWidget {...entities.block[block_id]} link />}
        invoices={state.invoices.map((invoice) => (
          <SourceInvoice invoice={invoice} direction={state.direction} key={invoice.id} />
        ))}
        source={sourceElement}
        rate={`${sepFormat(rate, false, true)}%`}
        invoiceable={sepFormat(invoiceable.toFixed(), true, false)}
        invoiced={sepFormat(invoiced.toFixed(), true, false)}
        balance={sepFormat(balance.toFixed(), true, false)}
        balanceControl={balanceControl}
        linked={<Amount name={key} bound={bound} />}
        linkedControl={boundControl}
        manualControl={manualControl}
      />
    );
  },
  (a: RowProps, b: RowProps): boolean =>
    a.data.rows[a.index].hash === b.data.rows[b.index].hash && a.data.visibleKeys === b.data.visibleKeys,
);
